import React, { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { withoutEmojis } from "../../../utils/emoji";
import useSendForInterpretation from "../../../hooks/useSendForInterpretation";
import TopUpCreditsModal from "../../../components/Credits/TopUpCreditsModal";

function SendForInterpretationDialog({
    open,
    onClose,
    EcgReport,
    items,
    selected,
    setSelected,
    Notes,
    setNotes,
    buttonLoading,
    handleSendForInterpretation,
}) {
    const hasSelection = !!selected?.value;
    const [paySendOpen, setPaySendOpen] = useState(false);
    const {
        credits: creditBalance,
        isUnlimitedCredits,
        outOfCredits,
        canPayAndSend,
        pricePerCredit,
        handlePayAndSend,
        paying: payAndSendLoading,
    } = useSendForInterpretation();

    const handlePaySendConfirm = async (quantity, paySendNotes) => {
        if (paySendNotes !== undefined) setNotes(paySendNotes);
        await handlePayAndSend(quantity, handleSendForInterpretation);
        setPaySendOpen(false);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 2,
                    overflow: "visible", // ensures full content visibility
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    pb: 1,
                    fontWeight: 600,
                    fontSize: "18px",
                    textAlign: "center",
                }}
            >
                Send for Interpretation
            </DialogTitle>
            <IconButton
                onClick={onClose}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "var(--app-text-muted)",
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ overflow: "visible", pb: 1 }}>
                {/* Patient Info Card */}
                <Box
                    sx={{
                        backgroundColor: "var(--app-surface)",
                        border: "1px solid var(--app-border)",
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        boxShadow: "inset 0 0 4px rgba(0,0,0,0.1)",
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        #{EcgReport?.collection_id} |{" "}
                        <span style={{ fontSize: "15px" }}>
                            {EcgReport?.user_data?.first_name}{" "}
                            {EcgReport?.user_data?.last_name}
                        </span>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {EcgReport?.user_data?.userAge} | {EcgReport?.user_data?.gender}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {moment(EcgReport?.createdAt).format("lll")}
                    </Typography>
                </Box>

                {/* Send To Dropdown */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel id="send-to-label">Send To</InputLabel>
                    <Select
                        labelId="send-to-label"
                        value={selected ? JSON.stringify(selected) : ""}
                        label="Send To"
                        onChange={(e) => {
                            const parsed = JSON.parse(e.target.value);
                            setSelected(parsed);
                        }}
                    >
                        {items.map((item) => (
                            <MenuItem
                                key={item.value}
                                value={JSON.stringify({
                                    value: item.value,
                                    userType: item.userType,
                                })}
                            >
                                {item.label} ({item.userType})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Notes TextArea */}
                <TextField
                    multiline
                    rows={3}
                    fullWidth
                    value={Notes || ""}
                    placeholder="Type something here..."
                    onChange={withoutEmojis((e) => setNotes(e.target.value))}
                    sx={{
                        mb: 2,
                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                />

                {/* Credit status */}
                <Typography
                    variant="caption"
                    sx={{ color: outOfCredits ? "error.main" : "text.secondary", display: "block" }}
                >
                    {outOfCredits
                        ? canPayAndSend
                            ? "You're out of interpretation credits. Pay to top up and send in one step."
                            : "Credits are pooled for the whole organisation - ask your account owner to top up."
                        : isUnlimitedCredits
                        ? "Unlimited interpretation credits available on your plan."
                        : `1 credit will be used · Balance ${creditBalance ?? "—"}`}
                </Typography>
            </DialogContent>

            {/* Footer */}
            <DialogActions sx={{ mt: 1, px: 3, pb: 2 }}>
                {hasSelection &&
                    (buttonLoading ? (
                        <Button
                            variant="contained"
                            disabled
                            fullWidth
                            sx={{
                                backgroundColor: "#1976d2",
                                textTransform: "none",
                            }}
                        >
                            <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                                style={{ marginRight: "8px" }}
                            ></span>
                            Sending Report...
                        </Button>
                    ) : outOfCredits && !canPayAndSend ? (
                        <Button variant="contained" disabled fullWidth sx={{ textTransform: "none" }}>
                            Insufficient credits
                        </Button>
                    ) : (
                        <Button
                            className="btn btn-added-primary"
                            variant="contained"
                            fullWidth
                            onClick={() => (outOfCredits ? setPaySendOpen(true) : handleSendForInterpretation())}
                            sx={{
                                backgroundColor: "#1976d2",
                                textTransform: "none",
                                "&:hover": { backgroundColor: "#1565c0" },
                            }}
                        >
                            {outOfCredits ? "Pay & Send" : "Send Report"}
                        </Button>
                    ))}
            </DialogActions>

            <TopUpCreditsModal
                open={paySendOpen}
                onClose={() => setPaySendOpen(false)}
                mode="paySend"
                currentBalance={creditBalance}
                pricePerCredit={pricePerCredit}
                report={EcgReport}
                recipientLabel={
                    items.find((item) => item.value === selected?.value)?.label || "Spandan MD Doctor"
                }
                loading={payAndSendLoading || buttonLoading}
                onPayAndSend={handlePaySendConfirm}
            />
        </Dialog>
    );
}

export default SendForInterpretationDialog;

SendForInterpretationDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    EcgReport: PropTypes.object,
    items: PropTypes.array.isRequired,
    selected: PropTypes.object,
    setSelected: PropTypes.func.isRequired,
    Notes: PropTypes.string,
    setNotes: PropTypes.func.isRequired,
    buttonLoading: PropTypes.bool,
    handleSendForInterpretation: PropTypes.func.isRequired,
};